export const trigIdentitiesLesson = {
  id: 7,
  title: "Trigonometric Identities",
  description: "Master Pythagorean, reciprocal, quotient, and cofunction identities to simplify and verify expressions",
  vocabulary: [
    "Identity",
    "Pythagorean Identity",
    "Reciprocal Identity",
    "Quotient Identity",
    "Cofunction Identity",
    "Even/Odd Identity",
    "Verify",
    "Simplify",
    "Fundamental Identities"
  ],
  conceptExplanation: `**Fundamental Trigonometric Identities:**

**Reciprocal Identities:**
• csc(θ) = 1/sin(θ)
• sec(θ) = 1/cos(θ)
• cot(θ) = 1/tan(θ)

**Quotient Identities:**
• tan(θ) = sin(θ)/cos(θ)
• cot(θ) = cos(θ)/sin(θ)

**Pythagorean Identities:**
• sin²(θ) + cos²(θ) = 1
• 1 + tan²(θ) = sec²(θ)
• 1 + cot²(θ) = csc²(θ)

**Cofunction Identities:**
• sin(90° - θ) = cos(θ)
• cos(90° - θ) = sin(θ)
• tan(90° - θ) = cot(θ)

**Even/Odd Identities:**
• sin(-θ) = -sin(θ) (odd)
• cos(-θ) = cos(θ) (even)
• tan(-θ) = -tan(θ) (odd)

**Strategies for Verifying Identities:**
1. Work with one side at a time (usually the more complex side)
2. Convert everything to sine and cosine
3. Use algebraic techniques (factor, combine fractions, multiply by conjugate)
4. Look for opportunities to use Pythagorean identities
5. Never move terms from one side to the other (not an equation to solve!)

**Common Techniques:**
• Factor when possible
• Multiply by conjugate to eliminate radicals
• Find common denominators
• Substitute using fundamental identities
• Split fractions`,
  examples: [
    {
      problem: "Verify the identity: tan(θ) · cos(θ) = sin(θ)",
      solution: `Step 1: Work with the left side
tan(θ) · cos(θ)

Step 2: Convert tan to sin/cos
= [sin(θ)/cos(θ)] · cos(θ)

Step 3: Simplify
= sin(θ) · [cos(θ)/cos(θ)]
= sin(θ) · 1
= sin(θ)

Step 4: This equals the right side ✓

Identity verified!`
    },
    {
      problem: "Simplify: (1 - sin²(θ))/cos(θ)",
      solution: `Step 1: Use Pythagorean identity
1 - sin²(θ) = cos²(θ)

Step 2: Substitute
= cos²(θ)/cos(θ)

Step 3: Simplify
= cos(θ)

Answer: cos(θ)`
    },
    {
      problem: "Verify: (sin(θ) + cos(θ))² = 1 + 2sin(θ)cos(θ)",
      solution: `Step 1: Expand the left side
(sin(θ) + cos(θ))²
= sin²(θ) + 2sin(θ)cos(θ) + cos²(θ)

Step 2: Rearrange
= [sin²(θ) + cos²(θ)] + 2sin(θ)cos(θ)

Step 3: Use Pythagorean identity
= 1 + 2sin(θ)cos(θ)

Step 4: This equals the right side ✓

Identity verified!`
    }
  ],
  teacherTip: "When verifying identities, NEVER treat it like an equation you're solving—you can't add or subtract from both sides! Instead, work with one side (usually the more complicated one) and transform it step-by-step until it matches the other side. My favorite trick: when stuck, convert everything to sine and cosine. Also, memorize the three Pythagorean identities—they're your most powerful tools!",
  questions: [
    {
      id: 1,
      question: "What is csc(θ) in terms of sin(θ)?",
      options: ["1/sin(θ)", "sin(θ)", "1/cos(θ)", "cos(θ)/sin(θ)"],
      correctAnswer: "1/sin(θ)",
      solution: "Reciprocal identity: csc(θ) = 1/sin(θ)"
    },
    {
      id: 2,
      question: "What is sec(θ) in terms of cos(θ)?",
      options: ["1/cos(θ)", "cos(θ)", "1/sin(θ)", "sin(θ)/cos(θ)"],
      correctAnswer: "1/cos(θ)",
      solution: "Reciprocal identity: sec(θ) = 1/cos(θ)"
    },
    {
      id: 3,
      question: "What is cot(θ) in terms of tan(θ)?",
      options: ["1/tan(θ)", "tan(θ)", "sin(θ)/cos(θ)", "cos(θ)"],
      correctAnswer: "1/tan(θ)",
      solution: "Reciprocal identity: cot(θ) = 1/tan(θ)"
    },
    {
      id: 4,
      question: "Express tan(θ) in terms of sin(θ) and cos(θ)",
      options: ["sin(θ)/cos(θ)", "cos(θ)/sin(θ)", "sin(θ) · cos(θ)", "1/sin(θ)"],
      correctAnswer: "sin(θ)/cos(θ)",
      solution: "Quotient identity: tan(θ) = sin(θ)/cos(θ)"
    },
    {
      id: 5,
      question: "Express cot(θ) in terms of sin(θ) and cos(θ)",
      options: ["cos(θ)/sin(θ)", "sin(θ)/cos(θ)", "sin(θ) · cos(θ)", "1/cos(θ)"],
      correctAnswer: "cos(θ)/sin(θ)",
      solution: "Quotient identity: cot(θ) = cos(θ)/sin(θ)"
    },
    {
      id: 6,
      question: "What is the Pythagorean identity relating sin and cos?",
      options: ["sin²(θ) + cos²(θ) = 1", "sin(θ) + cos(θ) = 1", "sin²(θ) - cos²(θ) = 1", "sin(θ) · cos(θ) = 1"],
      correctAnswer: "sin²(θ) + cos²(θ) = 1",
      solution: "Fundamental Pythagorean identity: sin²(θ) + cos²(θ) = 1"
    },
    {
      id: 7,
      question: "Simplify: 1 - cos²(θ)",
      options: ["sin²(θ)", "cos²(θ)", "tan²(θ)", "1"],
      correctAnswer: "sin²(θ)",
      solution: "From sin²(θ) + cos²(θ) = 1, we get sin²(θ) = 1 - cos²(θ)"
    },
    {
      id: 8,
      question: "Simplify: 1 - sin²(θ)",
      options: ["cos²(θ)", "sin²(θ)", "tan²(θ)", "sec²(θ)"],
      correctAnswer: "cos²(θ)",
      solution: "From sin²(θ) + cos²(θ) = 1, we get cos²(θ) = 1 - sin²(θ)"
    },
    {
      id: 9,
      question: "What is the Pythagorean identity relating tan and sec?",
      options: ["1 + tan²(θ) = sec²(θ)", "tan²(θ) + sec²(θ) = 1", "tan²(θ) - sec²(θ) = 1", "tan(θ) + sec(θ) = 1"],
      correctAnswer: "1 + tan²(θ) = sec²(θ)",
      solution: "Pythagorean identity: 1 + tan²(θ) = sec²(θ)"
    },
    {
      id: 10,
      question: "Simplify: sec²(θ) - 1",
      options: ["tan²(θ)", "sin²(θ)", "cos²(θ)", "cot²(θ)"],
      correctAnswer: "tan²(θ)",
      solution: "From 1 + tan²(θ) = sec²(θ), we get tan²(θ) = sec²(θ) - 1"
    },
    {
      id: 11,
      question: "What is the Pythagorean identity relating cot and csc?",
      options: ["1 + cot²(θ) = csc²(θ)", "cot²(θ) + csc²(θ) = 1", "cot²(θ) - csc²(θ) = 1", "cot(θ) + csc(θ) = 1"],
      correctAnswer: "1 + cot²(θ) = csc²(θ)",
      solution: "Pythagorean identity: 1 + cot²(θ) = csc²(θ)"
    },
    {
      id: 12,
      question: "Simplify: csc²(θ) - cot²(θ)",
      options: ["1", "0", "sin²(θ)", "cos²(θ)"],
      correctAnswer: "1",
      solution: "From 1 + cot²(θ) = csc²(θ), we get csc²(θ) - cot²(θ) = 1"
    },
    {
      id: 13,
      question: "What is sin(90° - θ)?",
      options: ["cos(θ)", "sin(θ)", "tan(θ)", "-sin(θ)"],
      correctAnswer: "cos(θ)",
      solution: "Cofunction identity: sin(90° - θ) = cos(θ)"
    },
    {
      id: 14,
      question: "What is cos(90° - θ)?",
      options: ["sin(θ)", "cos(θ)", "tan(θ)", "-cos(θ)"],
      correctAnswer: "sin(θ)",
      solution: "Cofunction identity: cos(90° - θ) = sin(θ)"
    },
    {
      id: 15,
      question: "What is sin(-θ)?",
      options: ["-sin(θ)", "sin(θ)", "cos(θ)", "-cos(θ)"],
      correctAnswer: "-sin(θ)",
      solution: "Odd function identity: sin(-θ) = -sin(θ)"
    },
    {
      id: 16,
      question: "What is cos(-θ)?",
      options: ["cos(θ)", "-cos(θ)", "sin(θ)", "-sin(θ)"],
      correctAnswer: "cos(θ)",
      solution: "Even function identity: cos(-θ) = cos(θ)"
    },
    {
      id: 17,
      question: "Simplify: sin(θ)/cos(θ)",
      options: ["tan(θ)", "cot(θ)", "sec(θ)", "csc(θ)"],
      correctAnswer: "tan(θ)",
      solution: "Quotient identity: sin(θ)/cos(θ) = tan(θ)"
    },
    {
      id: 18,
      question: "Simplify: cos(θ) · tan(θ)",
      options: ["sin(θ)", "cos(θ)", "tan(θ)", "1"],
      correctAnswer: "sin(θ)",
      solution: "cos(θ) · tan(θ) = cos(θ) · [sin(θ)/cos(θ)] = sin(θ)"
    },
    {
      id: 19,
      question: "Simplify: sin(θ) · csc(θ)",
      options: ["1", "0", "sin²(θ)", "cos(θ)"],
      correctAnswer: "1",
      solution: "sin(θ) · csc(θ) = sin(θ) · [1/sin(θ)] = 1"
    },
    {
      id: 20,
      question: "Simplify: tan(θ) · cot(θ)",
      options: ["1", "0", "tan²(θ)", "sin(θ)"],
      correctAnswer: "1",
      solution: "tan(θ) · cot(θ) = tan(θ) · [1/tan(θ)] = 1"
    },
    {
      id: 21,
      question: "Simplify: (sin²(θ) + cos²(θ))/cos(θ)",
      options: ["sec(θ)", "cos(θ)", "1", "tan(θ)"],
      correctAnswer: "sec(θ)",
      solution: "(sin²(θ) + cos²(θ))/cos(θ) = 1/cos(θ) = sec(θ)"
    },
    {
      id: 22,
      question: "Simplify: sec(θ) · cos(θ)",
      options: ["1", "0", "sec²(θ)", "cos²(θ)"],
      correctAnswer: "1",
      solution: "sec(θ) · cos(θ) = [1/cos(θ)] · cos(θ) = 1"
    },
    {
      id: 23,
      question: "Express sec(θ) in terms of sin(θ) only (using Pythagorean identity)",
      options: ["1/√(1 - sin²(θ))", "√(1 - sin²(θ))", "1/sin(θ)", "√(1 + sin²(θ))"],
      correctAnswer: "1/√(1 - sin²(θ))",
      solution: "sec(θ) = 1/cos(θ). From sin²(θ) + cos²(θ) = 1, cos(θ) = √(1 - sin²(θ)). So sec(θ) = 1/√(1 - sin²(θ))"
    },
    {
      id: 24,
      question: "Simplify: (1 + tan²(θ))/sec²(θ)",
      options: ["1", "tan²(θ)", "sec²(θ)", "cos²(θ)"],
      correctAnswer: "1",
      solution: "From 1 + tan²(θ) = sec²(θ), we have (1 + tan²(θ))/sec²(θ) = sec²(θ)/sec²(θ) = 1"
    },
    {
      id: 25,
      question: "Simplify: sin²(θ)/(1 - cos²(θ))",
      options: ["1", "sin²(θ)", "cos²(θ)", "tan²(θ)"],
      correctAnswer: "1",
      solution: "1 - cos²(θ) = sin²(θ), so sin²(θ)/sin²(θ) = 1"
    },
    {
      id: 26,
      question: "What is tan(-θ)?",
      options: ["-tan(θ)", "tan(θ)", "cot(θ)", "-cot(θ)"],
      correctAnswer: "-tan(θ)",
      solution: "Odd function identity: tan(-θ) = -tan(θ)"
    },
    {
      id: 27,
      question: "Simplify: (sin(θ) + cos(θ))² + (sin(θ) - cos(θ))²",
      options: ["2", "1", "0", "2sin(θ)cos(θ)"],
      correctAnswer: "2",
      solution: "Expand: sin²(θ) + 2sin(θ)cos(θ) + cos²(θ) + sin²(θ) - 2sin(θ)cos(θ) + cos²(θ) = 2sin²(θ) + 2cos²(θ) = 2(sin²(θ) + cos²(θ)) = 2(1) = 2"
    },
    {
      id: 28,
      question: "Simplify: cot(θ)/csc(θ)",
      options: ["cos(θ)", "sin(θ)", "tan(θ)", "sec(θ)"],
      correctAnswer: "cos(θ)",
      solution: "cot(θ)/csc(θ) = [cos(θ)/sin(θ)]/[1/sin(θ)] = [cos(θ)/sin(θ)] · sin(θ) = cos(θ)"
    },
    {
      id: 29,
      question: "Which identity would you use to simplify 1/(1 - sin²(θ))?",
      options: ["sin²(θ) + cos²(θ) = 1", "1 + tan²(θ) = sec²(θ)", "1 + cot²(θ) = csc²(θ)", "tan(θ) = sin(θ)/cos(θ)"],
      correctAnswer: "sin²(θ) + cos²(θ) = 1",
      solution: "Use sin²(θ) + cos²(θ) = 1 to get 1 - sin²(θ) = cos²(θ), so 1/(1 - sin²(θ)) = 1/cos²(θ) = sec²(θ)"
    },
    {
      id: 30,
      question: "Simplify: (tan(θ) + cot(θ)) · sin(θ) · cos(θ)",
      options: ["1", "sin(θ) + cos(θ)", "tan(θ)", "sec(θ)"],
      correctAnswer: "1",
      solution: "(tan(θ) + cot(θ)) · sin(θ) · cos(θ) = [sin(θ)/cos(θ) + cos(θ)/sin(θ)] · sin(θ) · cos(θ) = sin²(θ) + cos²(θ) = 1"
    }
  ]
};